import { ApolloError } from '@apollo/client';

import {
  AdditionalServicesAvailability,
  LoyaltyDiscount,
} from '@notino/web-tracking';

import {
  runTrackingEventViewItem,
  runExponeaFunctions,
} from '@containers/App/utils';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';

import {
  getProductView_productDetailByMasterId_variants,
  getProductView_productDetailByMasterId,
  getProductView_productDetailByMasterId_campaigns,
  getProductView_productDetailByMasterId_variants_customerBenefits,
} from '../queries/__generated__/getProductView';
import {
  productDetailViewFragment,
  productDetailViewFragment_variants,
} from '../queries/__generated__/productDetailViewFragment';
import { variantFragment } from '../queries/__generated__/variantFragment';

import { IEngraving } from './../../ProductListing/model';
import {
  MIN_PRICE_PROMO_LABEL,
  RRP_PRICE_PROMO_LABEL,
} from './hooks/usePriceLogic/tracking';
import { IModifaceEffectVariants } from './ModiFaceModal/model';

export const trackItemInExponea = (
  product: productDetailViewFragment,
  variant: productDetailViewFragment_variants
) => {
  runTrackingEventViewItem({
    id: variant.id,
    name: variant.name,
    code: variant.productCode,
    masterProductCode: product.code,
    brandName: product.brandInfo.name,
    gender: variant.gender,
    marketType: product.marketType,
    price: variant.price.value,
    kind: product.kind,
    categories: product.category,
    subcategories: product.subCategory,
    types: product.type,
    stockQuantity: variant.stockAvailability.code,
    attributes: variant.attributes,
  });
  runExponeaFunctions();
};

export const getModifaceEffectsForVariant = (
  modifaceMakeUpData: getProductView_productDetailByMasterId_variants['modifaceMakeUpData']
) => {
  return modifaceMakeUpData.reduce((acc, effect) => {
    // Loop through shades
    const shades = effect.shades.map((shade) => ({
      ...shade,
      category: effect.category,
    }));
    return [...acc, ...shades];
  }, []);
};

export const getModifaceVariants = (
  variants: getProductView_productDetailByMasterId_variants[]
): IModifaceEffectVariants => {
  return variants.reduce((result, { id, modifaceMakeUpData }) => {
    if (!modifaceMakeUpData || modifaceMakeUpData.length === 0) {
      return result;
    }

    // If it has modiface effect
    return {
      ...result,
      [id]: getModifaceEffectsForVariant(modifaceMakeUpData),
    };
  }, <IModifaceEffectVariants>{});
};

export const getModifaceAvailability = (
  hasWebcam: boolean,
  hasModiface: boolean
): AdditionalServicesAvailability => {
  if (!hasModiface) {
    return AdditionalServicesAvailability.notAvailable;
  }

  return hasWebcam
    ? AdditionalServicesAvailability.available
    : AdditionalServicesAvailability.hidden;
};

export const getTryItFirstAvailability = (
  cartTryItFirstShow: boolean,
  tryItFirstCampaigns: getProductView_productDetailByMasterId_campaigns[],
  selectedVariant: variantFragment,
  additionalServicesError: ApolloError
) => {
  if (additionalServicesError) {
    return AdditionalServicesAvailability.timeout;
  }

  if (!cartTryItFirstShow) {
    return AdditionalServicesAvailability.hidden;
  }

  if (
    tryItFirstCampaigns.some(
      (tryItFirstCampaign) =>
        tryItFirstCampaign.productId === selectedVariant.id
    )
  ) {
    return AdditionalServicesAvailability.available;
  }

  return AdditionalServicesAvailability.notAvailable;
};

export const getEngravingAvailability = (
  cartEngravingShow: boolean,
  engraving: IEngraving,
  additionalServicesError: ApolloError
): AdditionalServicesAvailability => {
  if (additionalServicesError) {
    return AdditionalServicesAvailability.timeout;
  }

  if (engraving.allowed) {
    return cartEngravingShow && engraving.config.isAvailable
      ? AdditionalServicesAvailability.available
      : AdditionalServicesAvailability.hidden;
  }

  return AdditionalServicesAvailability.notAvailable;
};

interface ITrackPageReloadParams {
  hasWebcam?: boolean;
  variant: getProductView_productDetailByMasterId_variants;
  product: getProductView_productDetailByMasterId;
  modifaceVariants: IModifaceEffectVariants;
  engravingAvailable: AdditionalServicesAvailability;
  tryItFirstAvailable: AdditionalServicesAvailability;
  giftAvailable: boolean;
  loyaltyDiscount?: LoyaltyDiscount;
  rrpShown: boolean;
  minimalPriceShown: boolean;
}
export const trackPageReload = ({
  variant,
  product,
  modifaceVariants = {},
  engravingAvailable,
  tryItFirstAvailable,
  giftAvailable,
  loyaltyDiscount,
  hasWebcam,
  rrpShown,
  minimalPriceShown,
}: ITrackPageReloadParams) => {
  dispatchTrackingEvent({
    event: 'view_item',
    products: [
      ProductEventWither()
        .withProduct(product)
        .withVariant(variant)
        .withAdditionalPromoLabels([
          rrpShown && RRP_PRICE_PROMO_LABEL,
          minimalPriceShown && MIN_PRICE_PROMO_LABEL,
        ])
        .withServices({
          engravingAvailable,
          tryItFirstAvailable,
          modifaceVariants,
          loyaltyDiscount,
          giftAvailable,
          hasWebcam,
        })
        .build(),
    ],
    _clear: true,
  });
};

export const getVariantDescription = (variant, product): string =>
  variant.attributes && variant.attributes.Master
    ? product.annotation
    : variant.variantName;

export const getLoyaltyDiscountState = (
  customerBenefits: getProductView_productDetailByMasterId_variants_customerBenefits
) => {
  if (customerBenefits?.availableDiscount) {
    return LoyaltyDiscount.available;
  }

  if (customerBenefits?.customerDiscount) {
    return LoyaltyDiscount.active;
  }

  return LoyaltyDiscount.notAvailable;
};

export const isGiftAvailable = (
  campaigns: getProductView_productDetailByMasterId_campaigns[]
) => {
  return Boolean(
    campaigns.find(
      (campaign) =>
        campaign.displayOnDetailPage &&
        (campaign.detailPageText || campaign.text)
    )
  );
};
